import { jsx, jsxs } from "react/jsx-runtime";
import { Dialog, Stack, Text, List, Box, TextLinkButton, IconInfo } from "braid-design-system";
import { createContext, useState, useContext } from "react";
import { useLanguage } from "@vocab/react";
import { formatMinorUnits } from "@seek/currency";
import { animatedBoxWrapper, animatedBox, slide, shine, border, content, flipAnimation } from "./RelevantApplications.css.mjs";
const TrackingContext = createContext({
  trackEvent: () => {
  }
});
const formatRoundedPrice = (price, currency, locale) => formatMinorUnits(price, {
  currency,
  locale,
  showFraction: true,
  notation: "compact"
});
const formatExactPrice = (price, currency, locale) => formatMinorUnits(price, {
  currency,
  locale,
  dropFraction: true
});
const getPriceWithTax = (price, tax) => {
  var _a;
  if (tax && tax.displayRecommendation === "inclusive") {
    const taxValue = ((_a = tax.amount) == null ? void 0 : _a.value) ?? 0;
    return {
      value: price.value === 0 ? price.value : price.value + taxValue,
      currency: price.currency
    };
  }
  return price;
};
const getDisplayPrices = (price) => ({
  displayRetailPrice: {
    price: getPriceWithTax(price.retailPrice, price.retailTax),
    tax: price.retailTax
  },
  displayNetPrice: {
    price: getPriceWithTax(price.netPrice, price.tax),
    tax: price.tax
  }
});
const isPriceDiscounted = (price) => price.type === "FixedPrice" && price.netPrice.value < price.retailPrice.value;
const isPriceFree = (price) => price.retailPrice.value === 0;
const isPriceHidden = (price) => price.type === "HiddenPrice";
const PRICE_TO_ROUND_AT = 1e8;
const isPriceRounded = (price) => price >= PRICE_TO_ROUND_AT;
const isPositiveCappedUnitPrice = (price) => Boolean(price.type === "CappedUnitPrice" && !isPriceFree(price.cappedPrice));
const isBillableUnitsHighlight = (variant) => variant === "billable-units-highlight" || variant === "billable-units-highlight-2";
const BillableUnitsDialog = ({
  isOpen,
  onClose,
  price,
  showRelevant
}) => {
  const { language } = useLanguage();
  if (!isPositiveCappedUnitPrice(price) || !isOpen) {
    return null;
  }
  const { displayNetPrice } = getDisplayPrices(price.unitPrice);
  const taxPrefix = {
    inclusive: "incl. ",
    exclusive: "+"
  };
  const taxType = displayNetPrice.tax && displayNetPrice.tax.displayRecommendation ? taxPrefix[displayNetPrice.tax.displayRecommendation] : void 0;
  const taxSuffix = displayNetPrice.tax ? `${taxType}${displayNetPrice.tax.code}` : void 0;
  const relevantOrQuality = showRelevant ? "relevant" : "quality";
  return /* @__PURE__ */ jsx(
    Dialog,
    {
      id: "billableUnitsInclusionsDialog",
      title: "Pay less if we don't deliver",
      open: isOpen,
      onClose,
      closeLabel: "Close",
      children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
        /* @__PURE__ */ jsx(Text, { size: "small", weight: "strong", children: "How does it work?" }),
        /* @__PURE__ */ jsxs(List, { space: "xsmall", children: [
          /* @__PURE__ */ jsxs(Text, { size: "small", children: [
            "Receive at least ",
            price.maxBillableUnits,
            " ",
            relevantOrQuality,
            " ",
            "applications"
          ] }),
          /* @__PURE__ */ jsxs(Text, { size: "small", children: [
            "Pay",
            " ",
            formatExactPrice(
              displayNetPrice.price.value,
              displayNetPrice.price.currency,
              language
            ),
            " ",
            taxSuffix,
            " less for each ",
            relevantOrQuality,
            " application short of",
            " ",
            price.maxBillableUnits
          ] })
        ] }),
        /* @__PURE__ */ jsx(Text, { size: "small", weight: "strong", children: showRelevant ? "What makes an application relevant?" : "What makes a quality application?" }),
        /* @__PURE__ */ jsxs(Text, { size: "small", children: [
          "SEEK determines a ",
          relevantOrQuality,
          " application as one which claims to have both:"
        ] }),
        /* @__PURE__ */ jsxs(List, { space: "xsmall", children: [
          /* @__PURE__ */ jsx(Text, { size: "small", children: "working rights where you're hiring" }),
          /* @__PURE__ */ jsx(Text, { size: "small", children: "2+ years' experience in the advertised role" })
        ] }),
        /* @__PURE__ */ jsxs(Text, { size: "small", children: [
          "You can still filter all applications you receive according to your criteria, however this will not affect the payment terms of",
          " ",
          relevantOrQuality,
          " applications."
        ] }),
        /* @__PURE__ */ jsx(Text, { tone: "neutral", size: "xsmall", children: "SEEK does not verify claims made in candidate applications. SEEK Pass verification is not required." })
      ] })
    }
  );
};
const shouldShowBillableUnits = (price) => isPositiveCappedUnitPrice(price) && isBillableUnitsHighlight(price.variant);
const RelevantApplications = ({
  price
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { trackEvent } = useContext(TrackingContext);
  if (!shouldShowBillableUnits(price)) return null;
  const onTooltipClick = () => {
    setIsOpen(true);
    trackEvent({
      type: "tooltip",
      action: "opened",
      name: "price-billable-units-highlight",
      content: "price-billable-units-highlight"
    });
  };
  const onTooltipClose = () => {
    setIsOpen(false);
  };
  const showRelevant = price.variant === "billable-units-highlight";
  const relevantOrQuality = showRelevant ? "relevant" : "quality";
  return /* @__PURE__ */ jsxs(
    Box,
    {
      background: "promoteLight",
      padding: "small",
      borderRadius: "standard",
      position: "relative",
      children: [
        /* @__PURE__ */ jsx(Box, { className: animatedBoxWrapper, children: /* @__PURE__ */ jsx(Box, { className: [animatedBox, slide], children: /* @__PURE__ */ jsx(Box, { height: "full", className: shine }) }) }),
        /* @__PURE__ */ jsx(Box, { className: border }),
        /* @__PURE__ */ jsx(Box, { className: content, data: { testId: "relevantAppsContent" }, children: /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
          /* @__PURE__ */ jsx(Text, { align: "center", size: "small", children: "Minimum of" }),
          /* @__PURE__ */ jsxs(Text, { weight: "strong", align: "center", children: [
            price.maxBillableUnits,
            " ",
            relevantOrQuality,
            " applications"
          ] }),
          /* @__PURE__ */ jsx(Text, { align: "center", size: "small", children: /* @__PURE__ */ jsxs(Box, { display: "inlineBlock", children: [
            "or pay less",
            " ",
            /* @__PURE__ */ jsx(
              TextLinkButton,
              {
                "aria-label": `Tooltip - more information about paying less if you don't receive the number of ${relevantOrQuality} applications`,
                weight: "weak",
                hitArea: "large",
                onClick: onTooltipClick,
                children: /* @__PURE__ */ jsx(Box, { display: "inlineBlock", className: flipAnimation, children: /* @__PURE__ */ jsx(IconInfo, {}) })
              }
            )
          ] }) })
        ] }) }),
        /* @__PURE__ */ jsx(
          BillableUnitsDialog,
          {
            isOpen,
            onClose: onTooltipClose,
            price,
            showRelevant
          }
        )
      ]
    }
  );
};
export {
  BillableUnitsDialog,
  RelevantApplications,
  TrackingContext,
  formatExactPrice,
  formatRoundedPrice,
  getDisplayPrices,
  isBillableUnitsHighlight,
  isPositiveCappedUnitPrice,
  isPriceDiscounted,
  isPriceFree,
  isPriceHidden,
  isPriceRounded,
  shouldShowBillableUnits
};
